import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLinkLogin } from './LinkLoginContext';

const VK_SDK_URL = 'https://unpkg.com/@vkid/sdk@<3.0.0/dist-sdk/umd/index.js';
const VK_APP_ID = Number(process.env.REACT_APP_VK_APP_ID || '54597378');
const VK_REDIRECT = (process.env.REACT_APP_VK_REDIRECT_URL || 'https://matchwear.ru').replace(/\/$/, '');

function loadVkSdk() {
  if ('VKIDSDK' in window && window.VKIDSDK) {
    return Promise.resolve(window.VKIDSDK);
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-vkid-sdk]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.VKIDSDK));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = VK_SDK_URL;
    s.async = true;
    s.dataset.vkidSdk = '1';
    s.onload = () => resolve(window.VKIDSDK);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

function formatVkError(err) {
  if (!err) return 'Ошибка VK ID';
  if (typeof err === 'string') return err;
  return err.message || err.error_description || err.detail || 'Ошибка VK ID';
}

export function VkBrowserLoginPanel() {
  const containerRef = useRef(null);
  const busyRef = useRef(false);
  const navigate = useNavigate();
  const { linkMode, linkToken, onSuccess } = useLinkLogin();
  const { loginVkId, token, refreshUser } = useAuth();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handlersRef = useRef({});
  handlersRef.current = {
    linkMode,
    effectiveLinkToken: linkMode ? linkToken || token : null,
    loginVkId,
    refreshUser,
    onSuccess,
    navigate,
  };

  useEffect(() => {
    let cancelled = false;
    let widget;

    const vkidOnError = (err) => {
      if (cancelled) return;
      setError(formatVkError(err));
      busyRef.current = false;
      setBusy(false);
    };

    const vkidOnSuccess = async (data) => {
      if (cancelled || busyRef.current) return;
      const {
        linkMode: isLink,
        effectiveLinkToken,
        loginVkId: login,
        refreshUser: refresh,
        onSuccess: onLinked,
        navigate: nav,
      } = handlersRef.current;

      busyRef.current = true;
      setBusy(true);
      setError(null);

      try {
        const accessToken = data?.access_token;
        if (!accessToken) {
          throw new Error('VK ID не вернул access_token');
        }

        await login(accessToken, effectiveLinkToken);

        if (isLink) {
          await refresh();
          if (onLinked) onLinked();
          else nav('/main/profile', { replace: true });
        } else {
          nav('/main/feed', { replace: true });
        }
      } catch (e) {
        vkidOnError(e);
      } finally {
        busyRef.current = false;
        if (!cancelled) setBusy(false);
      }
    };

    (async () => {
      try {
        await loadVkSdk();
        if (cancelled || !containerRef.current) return;
        if (!('VKIDSDK' in window)) {
          setError('Не удалось загрузить VK ID');
          return;
        }

        const VKID = window.VKIDSDK;

        VKID.Config.init({
          app: VK_APP_ID,
          redirectUrl: VK_REDIRECT,
          responseMode: VKID.ConfigResponseMode.Callback,
          source: VKID.ConfigSource.LOWCODE,
          scope: 'phone',
        });

        const oneTap = new VKID.OneTap();

        widget = oneTap
          .render({
            container: containerRef.current,
            showAlternativeLogin: true,
          })
          .on(VKID.WidgetEvents.ERROR, vkidOnError)
          .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload) => {
            VKID.Auth.exchangeCode(payload.code, payload.device_id)
              .then(vkidOnSuccess)
              .catch(vkidOnError);
          });
      } catch {
        if (!cancelled) setError('Не удалось загрузить VK ID');
      }
    })();

    return () => {
      cancelled = true;
      if (widget?.destroy) widget.destroy();
    };
  }, []);

  return (
    <div className="browser-login-card browser-login-card--vk">
      <div
        ref={containerRef}
        className="browser-login-card__vk-widget"
        aria-busy={busy}
        aria-label={linkMode ? 'Привязка VK ID' : 'Вход через VK ID'}
      />
      {busy ? (
        <p className="browser-login-card__vk-hint" role="status">
          {linkMode ? 'Привязка…' : 'Вход…'}
        </p>
      ) : null}
      {error ? <p className="browser-login-card__vk-error">{error}</p> : null}
    </div>
  );
}
